import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';

// ============ Types ============

export interface Booking {
  id: string;
  user_id: string;
  meeting_date: string;
  attendee_name?: string | null;
  attendee_email?: string | null;
  meeting_url?: string | null;
  status: string;
  booking_uid?: string | null;
  cal_booking_id?: number | null;
  created_at: string;
  updated_at: string;
}

// ============ Queries ============

export async function getUpcomingBookings(userId: string): Promise<ApiResult<Booking[]>> {
  const result = await supabase
    .from('bookings')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'scheduled')
    .gte('meeting_date', new Date().toISOString())
    .order('meeting_date', { ascending: true });

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  return ok(result.data || []);
}

// ============ Mutations ============

export async function syncCalBookings(): Promise<ApiResult<{ upcomingBookings?: Booking[] }>> {
  const { data, error } = await supabase.functions.invoke('sync-cal-bookings');
  
  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(data || {});
}

export async function cancelBooking(bookingId: string): Promise<ApiResult<null>> {
  const { error } = await supabase.functions.invoke('cancel-cal-booking', {
    body: { bookingId },
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(null);
}

export async function deleteAccount(): Promise<ApiResult<null>> {
  const { error } = await supabase.functions.invoke('delete-account');

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(null);
}

export async function createBooking(data: {
  user_id: string;
  meeting_date: string;
  attendee_email?: string | null;
  attendee_name?: string | null;
  booking_uid?: string | null;
  meeting_url?: string | null;
  status: string;
}): Promise<ApiResult<Booking>> {
  const result = await supabase.from('bookings').insert(data).select().single();

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  return ok(result.data);
}
